import { Button, fmt, Modal } from "@nabble/ui";
import { useEffect, useState } from "react";

export interface SessionExpiryDialogProps {
  open: boolean;
  expiresAt: number;
  onStay: () => Promise<unknown>;
  onLogout: () => void;
}

/** "Your session is about to expire" warning with a live countdown. */
export function SessionExpiryDialog({ open, expiresAt, onStay, onLogout }: SessionExpiryDialogProps): React.JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());
  const [staying, setStaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  async function stay(): Promise<void> {
    setStaying(true);
    try {
      await onStay();
    } finally {
      setStaying(false);
    }
  }

  const remaining = Math.max(0, expiresAt - now);

  return (
    <Modal
      open={open}
      size="sm"
      icon="clock"
      tone="warning"
      title="Your session is about to expire"
      description="For security, we sign out inactive sessions."
      dismissible={false}
      onClose={() => void stay()}
      actions={
        <>
          <Button label="Sign out" variant="secondary" onClick={onLogout} />
          <Button label="Stay signed in" loading={staying} onClick={() => void stay()} data-autofocus />
        </>
      }
    >
      <div className="nb-session-countdown" role="timer" aria-live="off">
        <span className="nb-muted">Time left</span>
        <strong className="nb-num">{fmt.countdown(remaining)}</strong>
      </div>
      <p className="nb-muted">If you don't respond, we'll sign you out and you'll need to sign in again. Anything you haven't saved will be lost.</p>
    </Modal>
  );
}
